import { FilterCriteria, ValueFilter } from '../../FilterStore'
import { FloatingTooltip } from '../../../Tooltip/FloatingTooltip'
import { FilterChipCloseButton, FilterChipContainer } from './FilterChipStyles'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

type FilterChipValue = {
  title?: string
  value: ValueFilter[]
  onClose?: () => void
  filterCriteria: FilterCriteria
  isHovered?: boolean
}

export function FilterChipValue({
  onClose,
  value,
  title,
  filterCriteria,
  isHovered,
}: FilterChipValue) {
  const isExclusive = filterCriteria === 'neither'
  const variant = useTableLinkVariant()
  const isPlain = variant === 'plain'
  const getFormattedValue = () => {
    if (value.length === 1) return value[0]?.label
    return `${value.length} selected`
  }

  // On DS2026 (Targets) surfaces, negative filters render "NOT" text on a
  // basil-tinted chip; pass $isExclusive=false so the styled-component's red
  // bg doesn't kick in. Elsewhere the original red-on-exclusive treatment
  // stays unchanged.
  const chip = (
    <FilterChipContainer
      $isExclusive={isPlain ? false : isExclusive}
      isHovered={isHovered}
    >
      <span>
        {`${title}: `}
        {isPlain && isExclusive ? <b>NOT </b> : null}
        <b>{getFormattedValue()}</b>
      </span>
      {onClose && (
        <FilterChipCloseButton
          $isExclusive={isPlain ? false : isExclusive}
          // Stop the click from bubbling into the FloatingTooltip's
          // reference, which would otherwise toggle the tooltip open as the
          // filter is being cleared.
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
        />
      )}
    </FilterChipContainer>
  )

  if (value.length <= 1) return chip

  // Click-trigger (not hover): the tooltip can scroll when many selections
  // are listed, so users need to move their cursor into the floating panel
  // without it dismissing. `useDismiss` inside FloatingTooltip closes it
  // again on any outside click.
  // Render each selection as a real block element so the tooltip has a
  // predictable height — much more reliable than a single text node with
  // \n + whitespace-pre-line, which some browsers don't size for overflow.
  const tooltipContent = (
    <div className="flex flex-col gap-0.5">
      {value.map((v, i) => (
        <div key={`${v.value}-${i}`} className="leading-5">
          {v.label}
        </div>
      ))}
    </div>
  )

  return (
    <FloatingTooltip
      text={tooltipContent}
      // Above the chip — chips live in the bottom-modal footer's wrapping
      // grid, and a tooltip rendered below would overlap the next row or fall
      // past the modal edge. `flip()` middleware still auto-flips below if
      // there's no room above.
      placement="top"
      trigger="click"
      theme="dark"
    >
      <span className="inline-flex cursor-pointer">{chip}</span>
    </FloatingTooltip>
  )
}
